import type {
  LandingUrlMonitorFeedback,
  LandingUrlMonitorFollowRequest,
  LandingUrlMonitorPageRequest,
  LandingUrlMonitorPageResponse,
  LandingUrlMonitorPageResult,
  LandingUrlMonitorQuery,
} from './type/landing-url-monitor'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

export const landingUrlMonitorApi = defineApi(
  'operation/landingDomainMonitor',
  ({ prefix }) => {
    /**
     * 查询投放 URL监控分页列表。
     * POST /gobestads/operation/landingDomainMonitor/queryRecordPage
     */
    function queryRecordPage(
      params: LandingUrlMonitorQuery,
    ): Promise<LandingUrlMonitorPageResult> {
      const condition = params.condition
      const merchantId = condition.merchantId?.trim()
      const customerId = merchantId ? Number(merchantId) : undefined
      const sortBy: LandingUrlMonitorPageRequest['sortBy'] = {
        accountId: 'accountId',
        discoveredAt: 'discoveredAt',
        feedbackAt: 'followOperatedAt',
        merchantId: 'customerId',
      }[params.sort?.field ?? ''] as LandingUrlMonitorPageRequest['sortBy']
      const request: LandingUrlMonitorPageRequest = {
        condition: {
          accountId: condition.accountId,
          customerId: Number.isInteger(customerId) ? customerId : undefined,
          customerName: condition.customerName,
          dateRange: condition.dateRange,
          followStatus: condition.feedbackStatus,
          registeredDomain: condition.domain,
        },
        orderBy: params.sort?.order?.toUpperCase() as
          | 'ASC'
          | 'DESC'
          | undefined,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy,
      }
      return requestClient
        .post<LandingUrlMonitorPageResponse>(prefix('queryRecordPage'), request)
        .then((response) => ({
          items: response.list.map((record) => ({
            accountId: record.accountId,
            accountName: record.accountName,
            amOpenId: record.amOpenId,
            bdOpenId: record.bdOpenId,
            customerName: record.customerName,
            discoveredAt: record.discoveredAt,
            domain: record.registeredDomain,
            feedbackAt: record.followOperatedAt ?? undefined,
            feedbackBy: record.followOperator,
            feedbackStatus: record.followStatus,
            id: String(record.id),
            merchantId: record.customerId,
            previousDomains: record.priorDomains,
            remark: record.followRemark,
            urls: record.loadUrls,
          })),
          total: response.total,
        }))
    }

    /**
     * 提交投放 URL监控反馈。
     * POST /gobestads/operation/landingDomainMonitor/follow
     * POST /gobestads/operation/landingDomainMonitor/batchFollow
     */
    function follow(ids: string[], feedback: LandingUrlMonitorFeedback) {
      const recordIds = ids.map((id) => {
        const recordId = Number(id)
        if (!Number.isInteger(recordId) || recordId < 1) {
          throw new Error(`无效的监控记录ID：${id}`)
        }
        return recordId
      })
      const request: LandingUrlMonitorFollowRequest = {
        followRemark: feedback.remark,
        followStatus: feedback.feedbackStatus,
      }
      if (recordIds.length === 1) {
        request.recordId = recordIds[0]
        return requestClient.post(prefix('follow'), request)
      }
      request.recordIds = recordIds
      return requestClient.post(prefix('batchFollow'), request)
    }

    return { follow, queryRecordPage }
  },
)
